import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQrCodeAccess } from '../../utils/useQrCodeAccess';
import { buildApiUrl } from '../../config/api';

export default function CollectionPage() {
    const { loading, error, participantId, envelopeId, tokenValid, token, uuid } = useQrCodeAccess();
    const navigate = useNavigate();
    const location = useLocation();
    const [responseText, setResponseText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const envelopeShort = uuid ? uuid.split('-')[0] : '';

    useEffect(() => {
        if (location.state?.editingText) {
            setResponseText(location.state.editingText);
        }
    }, [location.state]);

    const [accessDenied, setAccessDenied] = useState(false);
    useEffect(() => {
        async function validateAccess() {
            if (tokenValid && participantId && uuid) {
                // 1. Lê a chave digitada pelo participante
                let accessKey = '';
                try {
                    accessKey = localStorage.getItem(`access_key_${uuid}`) || '';
                } catch {}

                // 2. Lê o nickname salvo no objeto qrcode
                let savedNickname = '';
                let qrcodeData = null;
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 3. Se não houver nickname salvo, buscar do backend e salvar
                if (!savedNickname) {
                    try {
                        const resp = await fetch(buildApiUrl(`qrcode/${uuid}`));
                        if (resp.ok) {
                            const data = await resp.json();
                            savedNickname = data.nickname || '';
                            // Atualiza localStorage
                            const updatedQrcode = { ...(qrcodeData || {}), nickname: savedNickname };
                            localStorage.setItem(`qrcode_${uuid}` , JSON.stringify(updatedQrcode));
                        }
                    } catch {}
                }

                // 4. Recarrega nickname após possível atualização
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 5. Valida: access_key_${uuid} === nickname
                if (!accessKey || !savedNickname || accessKey !== savedNickname) {
                    setAccessDenied(true);
                }
            }
        }
        validateAccess();
    }, [tokenValid, participantId, uuid]);

    if (loading) {
        return <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}><CircularProgress /></Container>;
    }
    if (error || tokenValid === false || accessDenied) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Alert severity="error">{error || accessDenied ? 'Chave de acesso não informada ou incorreta.' : 'Token inválido. Acesso não autorizado.'}</Alert>
            </Container>
        );
    }
    if (tokenValid === null) {
        return null;
    }

    const handleSubmitText = async () => {
        setIsLoading(true);
        try {
            if (location.state?.isEditing) {
                // Atualiza texto no backend
                const response = await fetch(buildApiUrl(`submit/text/${participantId}/${envelopeId}`), {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ response_text: responseText }),
                });
                if (!response.ok) {
                    let errorMsg = `Erro ${response.status}`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData.detail || errorMsg;
                    } catch (e) { /* Ignore */ }
                    throw new Error(errorMsg);
                }
                navigate(`/review/${uuid}?token=${token}`, { state: { ...location.state, responseText: responseText, isEditing: false } });
                setIsLoading(false);
                return;
            }
            const response = await fetch(buildApiUrl(`submit/text/${participantId}/${envelopeId}`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ response_text: responseText }),
            });
            if (!response.ok) {
                let errorMsg = `Erro ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || errorMsg;
                } catch (e) { /* Ignore */ }
                throw new Error(errorMsg);
            }
            const result = await response.json();
            navigate(`/photos/${uuid}?token=${token}`, { state: { responseText: responseText, submissionId: result.submission_id } });
        } catch (err) {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>Sessão Envelope {envelopeShort}</Typography>
            <TextField
                label="Digite sua resposta aqui"
                multiline
                rows={10}
                fullWidth
                variant="outlined"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                sx={{ mb: 2 }}
            />
            <Button
                variant="contained"
                onClick={handleSubmitText}
                disabled={isLoading || !responseText.trim()}
                fullWidth
            >
                {isLoading ? <CircularProgress size={24} /> : (location.state?.isEditing ? 'Salvar Texto e Voltar para Revisão' : 'Enviar Resposta e Ir para Fotos')}
            </Button>
        </Container>
    );
}
